import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { DroneHarmonyGeodataProcessType } from '../../../../../../../../../typings/api/skymap/rest/v1/.common';
import { reset } from '../../../../../../utils/styled-reset';
import { List } from '../../../../../list/list';

const ProcessingPackagesInfo = ({
  geodataProcessType,
}: {
  geodataProcessType: DroneHarmonyGeodataProcessType;
}) => {
  const { t } = useTranslation();

  return (
    <Grid>
      {geodataProcessType === 'ORTHO_ONLY' && (
        <Card>
          <CardTitle>
            {t('setupDialog.cards.orthophoto.title', { ns: 'cloudProcessing' })}
          </CardTitle>
          <CardContent>
            {t('setupDialog.cards.orthophoto.text', { ns: 'cloudProcessing' })}
          </CardContent>
          <List
            items={[
              t('setupDialog.items.gcpAdjustment', { ns: 'cloudProcessing' }),
              t('setupDialog.items.orthophoto', { ns: 'cloudProcessing' }),
              t('setupDialog.items.dataExport', { ns: 'cloudProcessing' }),
            ]}
            marker="disc"
          />
        </Card>
      )}

      {geodataProcessType === 'FULL' && (
        <Card>
          <CardTitle>{t('setupDialog.cards.standard.title', { ns: 'cloudProcessing' })}</CardTitle>
          <CardContent>
            {t('setupDialog.cards.standard.text', { ns: 'cloudProcessing' })}
          </CardContent>
          <List
            items={[
              t('setupDialog.items.gcpAdjustment', { ns: 'cloudProcessing' }),
              t('setupDialog.items.model', { ns: 'cloudProcessing' }),
              t('setupDialog.items.orthophoto', { ns: 'cloudProcessing' }),
              t('setupDialog.items.pointCloud', { ns: 'cloudProcessing' }),
              t('setupDialog.items.dataExport', { ns: 'cloudProcessing' }),
            ]}
            marker="disc"
          />
        </Card>
      )}

      {geodataProcessType === 'POINTCLOUD_ONLY' && (
        <Card>
          <CardTitle>
            {t('setupDialog.cards.pointCloud.title', { ns: 'cloudProcessing' })}
          </CardTitle>
          <CardContent>
            {t('setupDialog.cards.pointCloud.text', { ns: 'cloudProcessing' })}
          </CardContent>
          <List
            items={[
              t('setupDialog.items.gcpAdjustment', { ns: 'cloudProcessing' }),
              t('setupDialog.items.orthophoto', { ns: 'cloudProcessing' }),
              t('setupDialog.items.pointCloud', { ns: 'cloudProcessing' }),
              t('setupDialog.items.dataExport', { ns: 'cloudProcessing' }),
            ]}
            marker="disc"
          />
        </Card>
      )}

      {geodataProcessType === 'FULL_HIGH_QUALITY' && (
        <Card>
          <CardTitle>{t('setupDialog.cards.premium.title', { ns: 'cloudProcessing' })}</CardTitle>
          <CardContent>
            {t('setupDialog.cards.premium.text', { ns: 'cloudProcessing' })}
          </CardContent>
          <List
            items={[
              t('setupDialog.items.gcpAdjustment', { ns: 'cloudProcessing' }),
              t('setupDialog.items.model', { ns: 'cloudProcessing' }),
              t('setupDialog.items.orthophoto', { ns: 'cloudProcessing' }),
              t('setupDialog.items.pointCloud', { ns: 'cloudProcessing' }),
              t('setupDialog.items.dataExport', { ns: 'cloudProcessing' }),
            ]}
            marker="disc"
          />
        </Card>
      )}

      {geodataProcessType === 'SIGNATURE' && (
        <Card>
          <CardTitle>{t('setupDialog.cards.signature.title', { ns: 'cloudProcessing' })}</CardTitle>
          <CardContent>
            {t('setupDialog.cards.signature.text', { ns: 'cloudProcessing' })}
          </CardContent>
          <List
            items={[
              t('setupDialog.items.gcpAdjustment', { ns: 'cloudProcessing' }),
              t('setupDialog.items.model', { ns: 'cloudProcessing' }),
              t('setupDialog.items.orthophoto', { ns: 'cloudProcessing' }),
              t('setupDialog.items.pointCloud', { ns: 'cloudProcessing' }),
              t('setupDialog.items.dataExport', { ns: 'cloudProcessing' }),
            ]}
            marker="disc"
          />
        </Card>
      )}
    </Grid>
  );
};

const Grid = styled.div`
  display: grid;
  gap: 1em;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  grid-auto-rows: repeat(auto, 3);
`;

const Card = styled.article`
  // Make sure grid-row spans the same number of columns specified in grid-auto-rows in parent
  // container. Use subgrid to align the variable sized card content.
  grid-row: span 3;
  grid-template-rows: subgrid;

  display: grid;
  gap: 1em;
  background-color: #fff3;
  border-radius: 0.5em;
  padding: 1em;
`;

const CardTitle = styled.h3`
  ${reset}
`;

const CardContent = styled.span`
  word-break: break-word;
`;

export { ProcessingPackagesInfo };
